import { Controller } from "@hotwired/stimulus"
import { getNotyf } from "../services/toast"

// Toast controller using Notyf library for reliable notifications
// Uses shared Notyf instance from toast service
// CSS is loaded from app/assets/stylesheets/vendor/_notyf.scss
export default class extends Controller {
  connect() {
    // Use shared Notyf instance
    this.notyf = getNotyf()

    // Process any flash messages on page load
    this.processFlashes()

    // Observe for new flash messages added via Turbo Stream
    this.observer = new MutationObserver(() => this.processFlashes())
    this.observer.observe(this.element, { childList: true, subtree: true })

    // Listen for turbo stream render events
    document.addEventListener("turbo:before-stream-render", this.handleTurboStream)

    // Listen for turbo:load to catch flashes after full page transitions
    document.addEventListener("turbo:load", this.handleTurboLoad)

    // Also listen for turbo:render to catch flashes immediately after render
    document.addEventListener("turbo:render", this.handleTurboLoad)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    document.removeEventListener("turbo:before-stream-render", this.handleTurboStream)
    document.removeEventListener("turbo:load", this.handleTurboLoad)
    document.removeEventListener("turbo:render", this.handleTurboLoad)
  }

  handleTurboLoad = () => {
    // Process flashes after Turbo page load
    requestAnimationFrame(() => this.processFlashes())
  }

  handleTurboStream = () => {
    // After turbo stream renders, check for new flashes
    setTimeout(() => this.processFlashes(), 50)
  }

  processFlashes() {
    const flashes = this.element.querySelectorAll("[data-toast-flash]")
    flashes.forEach(flash => {
      const type = flash.dataset.toastType || "info"
      const message = flash.dataset.toastMessage
      if (message) {
        this.show(type, message)
      }
      flash.remove()
    })
  }

  show(type, message) {
    switch (type) {
      case "success":
        this.notyf.success(message)
        break
      case "error":
        this.notyf.error(message)
        break
      case "warning":
        this.notyf.open({ type: "warning", message })
        break
      case "info":
      default:
        this.notyf.open({ type: "info", message })
        break
    }
  }

  // Public methods for programmatic use
  success(message) { this.show("success", message) }
  error(message) { this.show("error", message) }
  warning(message) { this.show("warning", message) }
  info(message) { this.show("info", message) }
}
